<template>
  <Dropdown class="edit-dropdown user-notifications" menu-right>
    <button
      type="button"
      class="btn btn-notification-sm relative"
      data-test="notifications-trigger-button"
      data-role="trigger"
      aria-label="Notifications"
    />
    <div
      v-show="uncompletedActionNotifications !== '0'"
      class="notification-badge"
      data-test="action-notification-count"
    >
      {{ uncompletedActionNotifications }}
    </div>

    <template #dropdown v-if="!error.length">
      <li>
        <h4 class="notification-heading">Notifications</h4>
      </li>
      <li role="separator" class="divider"></li>
      <li>
        <h5 v-if="recentNotifications.length" class="notification-heading">RECENT</h5>
      </li>
      <li
        v-for="notification in recentNotifications"
        :key="notification.id"
        ref="notificationsRef"
        data-test="recent-notifications"
      >
        <NotificationItem
          :notification="notification"
          @update-status="updateStatusAndRefetchData"
        />
      </li>
      <li>
        <h5 v-if="olderNotifications.length" class="notification-heading">OLDER</h5>
      </li>
      <li
        v-for="notification in olderNotifications"
        :key="notification.id"
        ref="notificationsRef"
        data-test="older-notifications"
      >
        <NotificationItem
          :notification="notification"
          @update-status="updateStatusAndRefetchData"
        />
      </li>
      <li role="separator" class="divider"></li>
      <li v-if="allNotificationsLoaded">
        <div class="end-notifications">
          <p class="text-center" data-test="all-loaded">
            That's all for your notifications from the last 30 days.
          </p>
          <img
            src="../../assets/notifications//flag-img.svg"
            alt="all notifications loaded"
            class="all-loaded-img"
          />
        </div>
      </li>
    </template>
    <template #dropdown v-else>
      <li class="notification-error">{{ error }}</li>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch, watchEffect, computed } from "vue";
import { Dropdown } from "uiv";
import dayjs from "dayjs";
import useNotification from "../../composables/useNotification";
import {
  NotificationApiModel,
  NotificationStatusApiModel,
  PaginatedResponseApiModel,
} from "../../api-models";
import { csContext } from "../../services/cs-context";
import NotificationItem from "./notification-item.vue";

const userId = csContext.user.id;

const INITIAL_PAGE = 1;
const PAGE_SIZE = 10;

const { getNotificationsForUser, updateStatus } = useNotification();

const notifications = ref<PaginatedResponseApiModel<NotificationApiModel>>();
const allNotificationsLoaded = ref(false);
const loading = ref(false);
const error = ref("");

const notificationsRef = ref<HTMLElement[]>();

const recentNotifications = computed(() => {
  const twoDaysAgo = dayjs().subtract(2, "day");

  if (notifications.value) {
    return notifications.value.items.filter((n) => {
      return dayjs(n.createdDate).isAfter(twoDaysAgo);
    });
  }
  return [];
});

const olderNotifications = computed(() => {
  const twoDaysAgo = dayjs().subtract(2, "day");

  if (notifications.value) {
    return notifications.value.items.filter((n) => {
      return dayjs(n.createdDate).isBefore(twoDaysAgo);
    });
  }
  return [];
});

const fetchNotifications = async (pageNumber: number, pageSize?: number) => {
  const pageSizeToUse = pageSize || PAGE_SIZE;
  const newNotifications = await getNotificationsForUser(userId, pageNumber, pageSizeToUse);
  if (notifications.value) {
    notifications.value.items.push(...newNotifications.items);
  } else {
    notifications.value = newNotifications;
  }

  nextTick(() => {
    if (notifications.value && notifications.value.items.length >= notifications.value.totalCount) {
      allNotificationsLoaded.value = true;
    }
  });
};

const updateStatusAndRefetchData = async (
  notification: NotificationApiModel,
  newStatus: NotificationStatusApiModel,
) => {
  const response = await updateStatus(notification.id, newStatus);
  if (response.success) {
    // refresh current notifications
    const currentNotificationCount = notifications.value?.items.length || 0;
    notifications.value = await getNotificationsForUser(
      userId,
      INITIAL_PAGE,
      currentNotificationCount,
    );
  }
};

const uncompletedActionNotifications = computed(() => {
  if (!notifications.value) {
    return "0";
  }

  const uncompleted = notifications.value.items.filter(
    (n) => n.actionStatus === "Uncompleted" || n.status === "Unread",
  );

  if (uncompleted.length === 0) {
    return "0";
  }

  if (uncompleted.length > 9) {
    return "9+";
  }

  return uncompleted.length.toString();
});

const observer = ref();

// Intersection Observer callback
const observerCallback = (entries) => {
  const lastEntry = entries[0];
  if (
    lastEntry.isIntersecting &&
    !allNotificationsLoaded.value &&
    !loading.value &&
    notifications.value
  ) {
    const nextPage = Math.ceil(notifications.value.items.length / PAGE_SIZE) + 1;
    fetchNotifications(nextPage);
  }
};

// Set the ref for the second-to-last notification
const setNotificationRef = (el: HTMLElement, index: number) => {
  if (notifications.value && index === notifications.value.items.length - 2) {
    if (observer.value) {
      observer.value.disconnect(); // Disconnect the previous observer
    }
    if (el) {
      observer.value.observe(el); // Observe the new second-to-last notification
    }
  }
};

// Watch for changes in the notifications list
watchEffect(() => {
  if (notifications.value && notificationsRef.value && notifications.value.items.length > 1) {
    const secondToLastIndex = notifications.value.items.length - 2;
    const secondToLastNotification = notificationsRef.value[secondToLastIndex];
    // We need to make sure the DOM is updated before we can assign the ref
    nextTick(() => {
      setNotificationRef(secondToLastNotification, secondToLastIndex);
    });
  }
});

watch(notifications, () => {
  if (notifications.value && notifications.value.page === 0) {
    error.value = "There was an error loading your notifications. Please try again later.";
  }
});

// Set up the Intersection Observer
onMounted(() => {
  observer.value = new IntersectionObserver(observerCallback, {
    threshold: 0.5,
  });

  fetchNotifications(INITIAL_PAGE);
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});
</script>

<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.navbar-right .dropdown-menu {
  right: -50px;
  left: auto;
}

.edit-dropdown.user-notifications.open > ul.dropdown-menu {
  max-height: 500px;
  overflow-y: scroll;
  min-width: 500px;

  @media (max-width: 670px) {
    min-width: 400px;
  }

  @media (max-width: @screen-xxs-max) {
    min-width: 85vw;
  }
}

.btn-group.user-notifications > .btn:first-child {
  border-radius: 100vmax !important;
}
</style>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.notification-heading {
  padding: 0 20px;
  font-weight: normal;
  font-size: 14px;
}

.end-notifications {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
}

.all-loaded-img {
  margin: auto;
}

.btn-notification-sm.relative {
  position: relative;
}

.notification-badge {
  position: absolute;
  top: 0;
  right: 0;
  width: 15px;
  height: 15px;
  aspect-ratio: 1;
  border-radius: 100vmax;
  background-color: @brand-primary;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  font-size: 10px;
  color: #ffffff;
}

.notification-error {
  padding: 0 20px;
  color: @brand-danger;
}
</style>
